// components/main/ObjekteListe.jsx

import React, { useState, useEffect } from "react";
import { useNavigate} from "react-router";
import { useAuth } from '../context/AuthContext.jsx';
import { useSelector, useDispatch } from "react-redux";
import { setSort, setCurrentPage, setTotalPages, resetFilters, setFilter, setJahrgangRange } from "../redux/filterState";
import Navbar from '../utils/Navbar.jsx';
import ObjektBild from '../utils/ObjektBild.jsx';
import ObjektKeyInfo from '../utils/ObjektKeyInfo.jsx';
import { useLoadActiveData } from "../../hooks/useLoadActiveData";
import ObjekteListeForm from "./ObjekteListeForm";
import FilterModal from '../utils/FilterModal.jsx';

const ObjekteListe = () => {
  const { user, fetchWithAuth, accessToken } = useAuth();
  const navigate = useNavigate();

  const [defaultsLoaded, setDefaultsLoaded] = useState(false);

  const [activeImage, setActiveImage] = useState([]);
  const [activeView, setActiveView] = useState({});

  const cleanInt = (v) => {
    return v === "" || v === undefined ? null : Number(v);
  };

  // redux components
  const dispatch = useDispatch();
  const filters = useSelector(state => state.filters);
  const [isRestoring, setIsRestoring] = useState(true);

  const { kategorie, ablageort, typ, land, material, waehrung, zustand, eigentuemer, sortField, sortDirection, currentPage, defaultKategorie } = filters;

  const [objekte, setObjekte] = useState([]);
 
  const [errorListe, setErrorListe] = useState(null);

  const {
    kategorienAktiv, ablageorteAktiv, typenAktiv, laenderAktiv,
    materialienAktiv, waehrungenAktiv, zustaendeAktiv, eigentuemerAktiv,
    loading, error
  } = useLoadActiveData({
      kategorie,
      ablageort,
      typ,
      land,
      material,
      waehrung,
      zustand,
      eigentuemer
  });

  // FilterModal components
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterModalMode, setFilterModalMode] = useState("load"); // load | save | delete
  const [filtersList, setFiltersList] = useState([]);

  const [selectedFilterId, setSelectedFilterId] = useState("");
  const [newFilterName, setNewFilterName] = useState("");


  const fetchObjekte = async (page = 1) => {
    try {
      const source = filters;
      const params = new URLSearchParams();

      params.set("page", page);
      params.set("ordering", source.sortDirection === "asc" ? source.sortField : `-${source.sortField}`);

      if (source.kategorie) params.append("kategorie", source.kategorie);
      if (source.ablageort) params.append("ablageort", source.ablageort);
      if (source.typ) params.append("typ", source.typ);
      if (source.land) params.append("land", source.land);
      if (source.material) params.append("material", source.material);
      if (source.waehrung) params.append("waehrung", source.waehrung);
      if (source.zustand) params.append("zustand", source.zustand);
      if (source.eigentuemer) params.append("eigentuemer", source.eigentuemer);
      if (filters.jahrgangMin) {
        params.append("jahrgang_min", filters.jahrgangMin);
      }
      if (filters.jahrgangMax) {
        params.append("jahrgang_max", filters.jahrgangMax);
      }

      const response = await fetchWithAuth(`/api/main/?${params}`, {
        headers: {
          "Content-Type": "application/json",
          }
      });
      const data = await response.json();

      // set page 1 when no rows exists (fallback, because already checked in 'setFilter' in redux)
      if (!data.results || data.results.length === 0) {
        setObjekte([]);
        if (page !== 1){
          dispatch(setCurrentPage(1));
        }
        return;
      }

      dispatch(setTotalPages(data.count, data.page_size));  // responses from api
      setObjekte(data.results);
    } catch (err) {
      setErrorListe(err.message);
    }
  };

  const fetchJahrgangRange = async () => {
    try {
      const res = await fetchWithAuth(`/api/main/jahrgang-range/`, {
        headers: {
          "Content-Type": "application/json"
        }
      });
      const data = await res.json();
      if (data.min_jahrgang && data.max_jahrgang) {
        dispatch(setJahrgangRange(data.min_jahrgang, data.max_jahrgang));
      }
    } catch (err) {
      console.warn("Fehler beim Laden des Jahrgangsbereichs:", err);
    }
  };

  // 1. useEffect: initialise and load page
  useEffect(() => {

    const restoreStateAndLoadDefaults = async () => {

      const saved = sessionStorage.getItem("lastKnownListState");

      if (saved) {

        const parsed = JSON.parse(saved);

        Object.entries(parsed).forEach(([field, value]) => {
          dispatch(setFilter(field, value));
        });

      }

      await fetchJahrgangRange();

      setDefaultsLoaded(true);
      setIsRestoring(false);
    };

    if (accessToken) {
      restoreStateAndLoadDefaults();
    }

  }, [accessToken]);

  // 2. useEffect: when changing form data (filter, sorting, pagination), with saving to session storage
  useEffect(() => {
    if (
      !accessToken ||
      !defaultsLoaded ||
      isRestoring
    ) return;

    sessionStorage.setItem(
      "lastKnownListState",
      JSON.stringify(filters)
    );

    fetchObjekte(filters.currentPage);

  }, [filters, accessToken, defaultsLoaded, isRestoring]);
  
  // initializing object images or objektkeyinfo frame
  useEffect(() => {
    if (objekte.length > 0) {
      const initial = {};

      objekte.forEach(o => {
        const hasBild01 = !!o.bild01;
        const hasBild02 = !!o.bild02;

        if (hasBild01) {
          initial[o.id] = "bild01";
        } else if (hasBild02) {
          initial[o.id] = "bild02";
        } else {
          initial[o.id] = "info";
        }
      });

      setActiveView(initial);
    }
  }, [objekte]);

  // save accordion state (opened/closed)
  useEffect(() => {
    let tries = 0;
    const maxTries = 20; // 20 × 50ms = 1 second max

    const interval = setInterval(() => {
      const collapseEl = document.getElementById("filterCollapse");

      if (collapseEl) {
        // console.log("DEBUG: collapseEl found =", collapseEl);

        const saved = sessionStorage.getItem("accordion_filter_open");
        // console.log("DEBUG: Saved accordion state =", saved);

        if (saved === "true") {
          collapseEl.classList.add("show");
        } else {
          collapseEl.classList.remove("show");
        }

        collapseEl.addEventListener("shown.bs.collapse", () => {
          // console.log("DEBUG: Accordion opened");
          sessionStorage.setItem("accordion_filter_open", "true");
        });

        collapseEl.addEventListener("hidden.bs.collapse", () => {
          // console.log("DEBUG: Accordion closed");
          sessionStorage.setItem("accordion_filter_open", "false");
        });

        clearInterval(interval);
      }

      tries++;
      if (tries > maxTries) {
        console.warn("DEBUG: collapseEl not found after waiting");
        clearInterval(interval);
      }
    }, 50);

    return () => clearInterval(interval);
  }, []);


  const toggleSort = (field) => {
    if (sortField === field) {
      dispatch(setSort(field, sortDirection === "asc" ? "desc" : "asc"));
    } else {
      dispatch(setSort(field, "asc"));
    }
  };

  const getSortSymbol = (field) => sortField === field ? (sortDirection === "asc" ? " ▲" : " ▼") : "";

  const getSortStyle = (field) => ({
    fontWeight: sortField === field ? 'bold' : 'normal',
    color: sortField === field ? '#007acc' : 'black',
    cursor: 'pointer'
  });

  const handleLinkClick = (id) => {
    navigate(`/${id}`);
  };

  // handle Pagination!
  const handlePageChange = (page) => {
    dispatch(setFilter("currentPage", page));
    fetchObjekte(page);
  };

  if (loading) return <div>Lade Objekte und Stammdaten...</div>;
  if (error || errorListe) return <div className="text-danger">Fehler: {error || errorListe}</div>;

  // get currency and country full names for showing in main list
  const getWaehrungName = (id) => waehrungenAktiv.find(w => w.id === id)?.name || "Unbekannt";
  const getLandName = (id) => laenderAktiv.find(l => l.id === id)?.name || "Unbekannt";
  const getMaterialName = (id) => materialienAktiv.find(l => l.id === id)?.name || "";

  // Desktop: Bild01 ↔ Bild02
  const toggleImage = (id, coin) => {
    const has01 = Boolean(coin.bild01);
    const has02 = Boolean(coin.bild02);

    if (!has01 || !has02) return; // 0 oder 1 Bild → kein Toggle

    setActiveImage(prev => {
      const current = prev[id] || "bild01";
      const next = current === "bild01" ? "bild02" : "bild01";
      return { ...prev, [id]: next };
    });
  };

  // Mobile: Bild01 → Bild02 → Info → Bild01 → …
  function getNextMobileView(coin, current) {
    const has01 = Boolean(coin.bild01);
    const has02 = Boolean(coin.bild02);

    if (!has01 && !has02) return "info";

    if (has01 && has02) {
      if (current === "bild01") return "bild02";
      if (current === "bild02") return "info";
      return "bild01";
    }

    // nur 1 Bild
    if (current === "info") return has01 ? "bild01" : "bild02";
    return "info";
  }

  const toggleView = (id, coin) => {
    setActiveView(prev => {
      const current =
        prev[id] ||
        (coin.bild01 ? "bild01" : coin.bild02 ? "bild02" : "info");

      const next = getNextMobileView(coin, current);
      return { ...prev, [id]: next };
    });
  };
  
  // load filter settings
  const openLoadFilterModal = async () => {
    try {
      const res = await fetchWithAuth("/api/data/filter/list/");
      const data = await res.json();
      const list = data.results || [];

      if (list.length === 0) {
        alert("Keine gespeicherten Filter vorhanden.");
        return;
      }

      setFiltersList(list);
      setFilterModalMode("load");
      setSelectedFilterId("");
      setNewFilterName("");

      setShowFilterModal(true);

    } catch (err) {
      console.error("Fehler beim Laden der Filterliste:", err);
    }
  };

  // load filter settings, afterwards
  const confirmLoadFilter = async () => {
    try {
      if (!selectedFilterId) return;

      const res = await fetchWithAuth(`/api/data/filter/load/${selectedFilterId}/`);
      const selected = await res.json();

      if (!res.ok) {
        alert("Filter nicht gefunden.");
        return;
      }

      // Redux setzen
      Object.entries(selected).forEach(([field, value]) => {
        dispatch(setFilter(field, value));
      });

      dispatch(setFilter("jahrgangMin", selected.jahrgang_min));
      dispatch(setFilter("jahrgangMax", selected.jahrgang_max));

      setShowFilterModal(false);

    } catch (err) {
      console.error("Fehler beim Laden des Filters:", err);
    }
  };

  // create and save filter settings
  const openSaveFilterModal = async () => {
    try {
      const res = await fetchWithAuth("/api/data/filter/list/");
      const data = await res.json();
      const list = data.results || [];

      setFiltersList(list);
      setFilterModalMode("save");
      setSelectedFilterId("");
      setNewFilterName("");

      setShowFilterModal(true);

    } catch (err) {
      console.error("Fehler beim Laden der Filterliste:", err);
    }
  };

  // create and save filter settings, afterwards save, for confirming
  const confirmSaveFilter = async () => {
    try {
      // 1. Validate name
      if (!newFilterName && !selectedFilterId) {
        alert("Bitte einen Namen eingeben.");
        return;
      }

      // 2. Build payload
      const payload = {
        name: newFilterName || filtersList.find(f => f.id == selectedFilterId)?.name,

        kategorie: filters.kategorie || null,
        ablageort: filters.ablageort || null,
        typ: filters.typ || null,
        land: filters.land || null,
        material: filters.material || null,
        waehrung: filters.waehrung || null,
        zustand: filters.zustand || null,
        eigentuemer: filters.eigentuemer || null,

        jahrgang_min: filters.jahrgangMin || null,
        jahrgang_max: filters.jahrgangMax || null,

        sortField: filters.sortField || null,
        sortDirection: filters.sortDirection || "asc"
      };

      let res;

      if (selectedFilterId) {
        // overwrite existing filter
        res = await fetchWithAuth(`/api/data/filter/save/${selectedFilterId}/`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload)
        });

      } else {
        // create new filter
        res = await fetchWithAuth(`/api/data/filter/create/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload)
        });
      }

      if (!res.ok) {
        const err = await res.json();
        console.error("Save error:", err);
        alert("Fehler beim Speichern.");
        return;
      }

      setShowFilterModal(false);

    } catch (err) {
      console.error("Fehler beim Speichern des Filters:", err);
    }
  };

  // delete filter
  const openDeleteFilterModal = async () => {
    try {
      const res = await fetchWithAuth("/api/data/filter/list/");
      const data = await res.json();
      const list = data.results || [];

      if (list.length === 0) {
        alert("Keine gespeicherten Filter vorhanden.");
        return;
      }

      setFiltersList(list);
      setFilterModalMode("delete");
      setSelectedFilterId("");
      setNewFilterName("");

      setShowFilterModal(true);

    } catch (err) {
      console.error("Fehler beim Laden der Filterliste:", err);
    }
  };

  // delete filter, afterwords, confirm deletion
  const confirmDeleteFilter = async () => {
    try {
      if (!selectedFilterId) return;

      const res = await fetchWithAuth(`/api/data/filter/delete/${selectedFilterId}/`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json"
        }
      });

      if (!res.ok) {
        alert("Fehler beim Löschen.");
        return;
      }

      // Modal schließen
      setShowFilterModal(false);

      // Optional: Liste aktualisieren
      const reload = await fetchWithAuth("/api/data/filter/list/");
      const data = await reload.json();
      setFiltersList(data.results || []);

    } catch (err) {
      console.error("Fehler beim Löschen des Filters:", err);
    }
  };


  return (
    <>
      <Navbar
        kategorien={kategorienAktiv}
        selectedKategorie={filters.kategorie}
        onKategorieChange={(newId) => dispatch(setFilter("kategorie", newId))}
      />
      <ObjekteListeForm
        objekte={objekte}
        getSortStyle={getSortStyle}
        getSortSymbol={getSortSymbol}
        toggleSort={toggleSort}
        handleLinkClick={handleLinkClick}

        getWaehrungName={getWaehrungName}
        getLandName={getLandName}
        getMaterialName={getMaterialName}

        kategorien={kategorienAktiv}
        ablageorte={ablageorteAktiv}
        typen={typenAktiv}
        laender={laenderAktiv}
        materialien={materialienAktiv}
        waehrungen={waehrungenAktiv}
        zustaende={zustaendeAktiv}
        eigentuemerdata={eigentuemerAktiv}

        // Redux components
        filters = {filters}
        dispatch = {dispatch}
        resetFilter={() => {
          dispatch(resetFilters());
        }}
        onPageChange={handlePageChange}   // pagination
        toggleImage={toggleImage}
        activeImage={activeImage}
        toggleView={toggleView}
        activeView={activeView}
        // loadFilter={loadFilter}        old version
        // saveFilter={saveFilter}        old version
        // deleteFilter={deleteFilter}    old version
        onLoadFilter={openLoadFilterModal}
        onSaveFilter={openSaveFilterModal}
        onDeleteFilter={openDeleteFilterModal}
      />
      <FilterModal
        show={showFilterModal}
        mode={filterModalMode}
        filtersList={filtersList}

        selectedFilterId={selectedFilterId}
        setSelectedFilterId={setSelectedFilterId}

        newFilterName={newFilterName}
        setNewFilterName={setNewFilterName}

        onClose={() => setShowFilterModal(false)}
        onConfirm={
          filterModalMode === "load"
            ? confirmLoadFilter
            : filterModalMode === "save"
            ? confirmSaveFilter
            : confirmDeleteFilter
        }
      />
    </>
  );
};

export default ObjekteListe;
